// src/utils/layout/createDocumentContent.js
//
// Dokuman adi ve verisinden THREE.js DXF entity listesi ureten factory.

import { createTekHatSema } from '../generators/generateTekHatSema';
import { Rectangle, Line, Text } from '../generators/utils';
import { createYuklemeCetvel } from '../generators/generateYuklemeCetvel';
import { createYuklemeCetvelMultiMeter } from '../generators/generateYuklemeCetvelMultiMeter';
import { createGerilimDusum } from '../generators/generateGerilimDusum';
import { createTopraklamaHesab } from '../generators/generateTopraklamaHesap';
import { createToprakalamaDetay } from '../generators/generateTopraklamaDetay';
import { createKolonSema } from '../generators/generateKolonSema';
import { createKolonSemaMultiFloor } from '../generators/generateKolonSemaMultiFloor';
import { createKabloBacasi } from '../generators/generateKabloBacasi';
import { createSembolListe } from '../generators/generateSembolListesi';
import { createPanoDetay } from '../generators/generatePanoDetay';
import { createNotlar } from '../generators/generateNotlar';
import { createBaslik } from '../generators/generateBaslik';
import { PROJECT_TYPES } from '../../../constants/projectTypes';
import { OLE_ACTUAL_H_CACHE } from './layoutConstants';

export function createDocumentContent(name, def, data, projectType = PROJECT_TYPES.SINGLE_UNIT) {
  switch (name) {
    case 'Kolon \u015eemas\u0131':
      if (projectType === PROJECT_TYPES.MULTI_FLOOR_SINGLE_METER) {
        return createKolonSemaMultiFloor(data || {});
      }
      // MULTI_METER: i\u00e7erik special builder (buildKolonSemaMultiMeter) ile \u00e7izilir.
      // Burada createKolonSema'y\u0131 da d\u00f6nd\u00fcrmek \u00c7\u0130FT render demek \u2192 Kolon documentGroup'u
      // iki diyagram (kolon + tek-hat g\u00f6r\u00fcn\u00fcml\u00fc) i\u00e7eriyor, tek dok\u00fcman gibi davran\u0131yor
      // (Ctrl+M birlikte s\u00fcr\u00fckleniyor) ve i\u00e7 i\u00e7e giriyor. Pano Detay ile ayn\u0131 desen:
      // MULTI_METER'da bo\u015f i\u00e7erik d\u00f6nd\u00fcr, special builder doldursun.
      if (projectType === PROJECT_TYPES.MULTI_METER) {
        return { result: [], width: def.w, height: def.h };
      }
      return createKolonSema(data || []);
    case 'TekHat  \u015eemas\u0131': {
      // createTekHatSema i\u00e7eri\u011fi Y-pozitif uzayda \u00e7izilir; buildSingleDocument'in
      // Y-down \u00e7er\u00e7evesi i\u00e7erikle hizalanm\u0131yor (\u00e7er\u00e7eve i\u00e7eri\u011fin alt\u0131nda kal\u0131yordu).
      // Bu y\u00fczden hasFrame:false + \u00e7er\u00e7eveyi BURADA i\u00e7eri\u011fin bbox'\u0131na g\u00f6re \u00e7iziyoruz
      // (ayn\u0131 koordinat uzay\u0131nda \u2192 hizal\u0131). createTekHatSema Kolon \u015eemalar\u0131'nda da
      // kullan\u0131ld\u0131\u011f\u0131 i\u00e7in \u00e7er\u00e7eve oraya de\u011fil, sadece bu standalone doc'a eklenir.
      const tek = createTekHatSema(data || []);
      if (!tek?.result?.length) return tek;
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      const acc = (x, y) => { if (typeof x === 'number' && typeof y === 'number') { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; } };
      for (const e of tek.result) {
        if (Array.isArray(e.vertices)) e.vertices.forEach(v => acc(v.x, v.y));
        // Text: yalnızca startPoint. endPoint = startPoint.y + 200 (metin yönü için
        // sahte nokta) — bbox'a dahil edince çerçeveyi ~200 birim fazla yükseltiyor.
        if (e.startPoint) acc(e.startPoint.x, e.startPoint.y);
        if (e.center) acc(e.center.x, e.center.y);
      }
      if (!Number.isFinite(minX)) return tek;
      const pad = 30, titleH = 50;
      const fx = minX - pad, fy = minY - pad;
      const fw = (maxX - minX) + 2 * pad, fh = (maxY - minY) + 2 * pad + titleH;
      const fopt = { color: 0xffffff, layer: '0', shape: true, hasContinuousLinetypePattern: false };
      // Grup etiketi: "NKT1-NKT3-NKT5 Tek Hat" biçiminde; tek panel ise "Tek Hat Şeması"
      const groupNames = def?.tekHatGroupNames;
      const titleText = groupNames?.length > 0
        ? groupNames.join('-') + ' Tek Hat'
        : 'TekHat  \u015eemas\u0131';
      const titleHandle = 'tekhat_title_' + (groupNames?.[0] || 'single');
      const frame = [
        Rectangle(fx, fy, fw, fh, fopt),
        Line(fx, fy + fh - titleH, fx + fw, fy + fh - titleH, fopt),
        Text(fx + fw / 2 - 90, fy + fh - titleH / 2 - 7, { text: titleText, textHeight: 14, handle: titleHandle }, 0, fopt),
      ];
      return { result: [...frame, ...tek.result], width: fw, height: fh };
    }
    case 'Y\u00fckleme Cetveli': {
      const cachedH = OLE_ACTUAL_H_CACHE.get('Y\u00fckleme Cetveli');
      // MULTI_METER ve SINGLE_UNIT i\u00e7in OLE (xlsx) zinciri. Excel template'leri
      // ayr\u0131 (yuklemeCetveliMultiMeter / yuklemeCetveliSingleUnit) \u2014 template
      // ad\u0131 oleHandler.js:17'de projectType'a g\u00f6re se\u00e7iliyor. Aksi halde
      // SINGLE_UNIT'te sync createYuklemeCetvel'\u0131n bozuk/eski DXF render'\u0131
      // sahnede yer al\u0131yor ama g\u00f6r\u00fcnm\u00fcyordu.
      // OLE2 (xlsx) zinciri: SINGLE_UNIT, MULTI_METER ve MULTI_FLOOR_SINGLE_METER (B).
      // Veri (transformSimpleSupplyToYC_Data -> {panels, adp}) adaptYCDataToXlsx ile uyumlu;
      // template oleHandler'da projectType'a gore secilir. B -> default 'yuklemeCetveli'.
      // NATIVE GEOMETRI: OLE2 AutoCAD'de render edilemedi (docs/OLE2_DXF_EXPORT_INVESTIGATION.md).
      // Doküman LINE/TEXT/RECTANGLE olarak çizilir → AutoCAD'de kesin render.
      if (projectType === PROJECT_TYPES.MULTI_METER) {
        return createYuklemeCetvelMultiMeter(0, 0, def.w, def.h, data || {});
      }
      // Aynı imza bug'ı (X, Y, W, H, data) — diğer tipler (örn. HIGH_RISE) için.
      const ret = createYuklemeCetvel(0, 0, def.w, def.h, data || {});
      if (cachedH) ret.height = cachedH;
      return ret;
    }
    case 'Topraklama Hesab\u0131': {
      const cachedH = OLE_ACTUAL_H_CACHE.get('Topraklama Hesab\u0131');
      return createTopraklamaHesab(data || {});
    }
    case 'Topraklama Detaylar\u0131':
      return createToprakalamaDetay();
    case 'Kablo Bacas\u0131':
      return createKabloBacasi(data || {});
    case 'Gerilim D\u00fc\u015f\u00fcm\u00fc':
      return createGerilimDusum(0, -400, def.w, def.h, data || []);
    case 'Sembol Listesi':
      return createSembolListe();
    case 'Pano Detay':
      if (projectType === PROJECT_TYPES.MULTI_METER) {
        return { result: [], width: def.w, height: def.h };
      }
      return createPanoDetay(data || []);
    case 'Enerji Odasi':
      return { result: [], width: def.w, height: def.h };
    case 'Notlar': {
      const state = require('../../../redux/store').store?.getState();
      const notlarTplId = state?.baslikTemplate?.activeNotlarTemplateId;
      const notlarTpl = notlarTplId ? state?.baslikTemplate?.templates?.find(t => t.id === notlarTplId) : null;
      if (notlarTpl?.dxfRawText) {
        return { result: [], width: def.w, height: def.h };
      }
      return createNotlar();
    }
    case 'Baslik': {
      const state = require('../../../redux/store').store?.getState();
      const tplId = state?.baslikTemplate?.activeTemplateId;
      if (tplId) {
        return { result: [], width: def.w, height: def.h };
      }
      return createBaslik();
    }
    default:
      return { result: [], width: def.w, height: def.h };
  }
}
